import { useState, createContext, FC, useContext, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { compareUsers, convertSetsToArrays, getAccessIcon, getConfig } from 'utils/helperMethods';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from './ConfigContext';
import { DimensionConfigType, DimensionDropdownType, PricingReport, ScenarioComparison } from 'types/pricingSimulator';
import { BreadcrumbItem } from 'components/atoms/Breadcrumb';

import { ConfigKeys } from 'utils/Enums';
import { pricingAnalyticsUrls, pricingSimulatorUrls } from 'config/urls';
import { useFetchLenseConfigAnalytics } from 'hooks/pricingSimulator/useFetchLenseConfigAnalytics';
import { queryClient } from 'services/reactQueryService';
import { useFetchGlobalKPIsData } from 'hooks/pricingSimulator/analytics/useFetchGlobalKpisData';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useFetchDimensionConfigData } from 'hooks/pricingSimulator/useFetchDimensionConfigData';
import { pricingSimulatorWrapperDataContext } from './PricingSimulatorWrapperContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';

const PricingAnalyticsDataContext = createContext<{
    viewMode: boolean;
    reportId: number;
    selectedReportInfo: PricingReport | null;
    scenarioComparisonState: ScenarioComparison | null;
    setScenarioComparisonState: Function;
    setSelectedReportInfo: Function;
    loggedInUser: string; // logged in user email id

    // helper filter to limit the data size while viewing
    scenarioFilter: any; // todo remove it
    setScenarioFilter: Function;
    searchKey: string;
    setSearchKey: Function;

    // SCENARIO FILTER PROPS
    availableFilters: any[];
    allCheckedAttribute: any;
    setAllCheckedAttribute: Function;
    allSelectedValueForAllAttribute: any;
    setAllSelectedValueForAllAttribute: Function;
    applyFilterObject: any;
    setApplyFilterObject: Function;
    resetFilterObject: Function;
    showIsEditedRecords: boolean;
    setShowIsEditedRecords: Function;
    openScenarioPopup: boolean;
    setOpenScenarioPopup: Function;

    updateReportProperty: <K extends keyof PricingReport>(key: K, value: PricingReport[K]) => void;
    updateComparisonProperty: <K extends keyof ScenarioComparison>(key: K, value: ScenarioComparison[K]) => void;

    breadcrumbItems: BreadcrumbItem[];
    showPercentageChange: boolean;
    setShowPercentageChange: Function;
    showOldValue: boolean;
    setShowOldValue: Function;

    lenseConfigLoading: boolean;
    globalKpiLoading: boolean;
    pricingAnalyticsIndexScreenUrl: string;
    isUpdate: boolean;
    filters: any;
    selectedDimensionListValues: string[];
    isDropdownAtLowestLevel: boolean;
    saveFilterConfig: Function;
    hasReportAccess: boolean;
    reportAccessLoading: boolean;
    resetReportQueries: Function;
}>({
    viewMode: false,
    reportId: 0,
    selectedReportInfo: null,
    setSelectedReportInfo: () => {},
    scenarioComparisonState: null,
    setScenarioComparisonState: () => {},
    loggedInUser: '',
    scenarioFilter: undefined,
    setScenarioFilter: () => {},
    searchKey: '',
    setSearchKey: () => {},
    availableFilters: [],
    allCheckedAttribute: () => {},
    setAllCheckedAttribute: () => {},
    allSelectedValueForAllAttribute: undefined,
    setAllSelectedValueForAllAttribute: () => {},
    applyFilterObject: undefined,
    setApplyFilterObject: () => {},
    resetFilterObject: () => {},
    showIsEditedRecords: false,
    setShowIsEditedRecords: () => {},
    openScenarioPopup: false,
    setOpenScenarioPopup: () => {},
    updateReportProperty: () => {},
    updateComparisonProperty: () => {},
    breadcrumbItems: [],
    showPercentageChange: false,
    setShowPercentageChange: () => {},
    showOldValue: false,
    setShowOldValue: () => {},
    lenseConfigLoading: false,
    globalKpiLoading: false,
    pricingAnalyticsIndexScreenUrl: '',
    isUpdate: false,
    filters: {},
    selectedDimensionListValues: [],
    isDropdownAtLowestLevel: false,
    saveFilterConfig: () => {},
    resetReportQueries: () => {},
    hasReportAccess: false,
    reportAccessLoading: false,
});
interface props {
    children: React.ReactNode;
}

const PricingAnalyticsContext: FC<props> = ({ children }) => {
    const { appConfig } = useContext(UserContext);
    const screenDetailsWithRoutes = getConfig('screenDetailsWithRoutes', appConfig);
    const pricingSimulatorConfig = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);

    const { enablePrivatePublicToggle } = pricingSimulatorConfig;

    const [searchKey, setSearchKey] = useState<any>({});
    const [openScenarioPopup, setOpenScenarioPopup] = useState(false);

    //importing config values from wrapper context
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const scenarioFilterKeys = dimensionConfigData?.scenarioFilterKeys ?? [];

    // scenario details
    const [selectedReportInfo, setSelectedReportInfo] = useState<PricingReport | null>(null);
    const [scenarioFilter, setScenarioFilter] = useState<any>(null);
    const [showPercentageChange, setShowPercentageChange] = useState(false);
    const [showOldValue, setShowOldValue] = useState(false);

    //comparison state
    const [scenarioComparisonState, setScenarioComparisonState] = useState<ScenarioComparison | null>(null);

    const [showIsEditedRecords, setShowIsEditedRecords] = useState<boolean>(false);
    const [hasReportAccess, setHasReportAccess] = useState(false);

    // scenario filter states
    const [allCheckedAttribute, setAllCheckedAttribute] = useState<any>({});
    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>([]);
    const [allSelectedValueForAllAttribute, setAllSelectedValueForAllAttribute] = useState({});
    const [applyFilterObject, setApplyFilterObject] = useState<any>({});

    const { mode, reportId } = useParams(); //scenario id,projectId from url will always be a string

    const isUpdate = useMemo(() => {
        return Boolean(compareUsers(selectedReportInfo?.createdBy ?? ''));
    }, [selectedReportInfo?.createdBy]);

    const filters = useMemo(() => {
        return transformFilterObjectWithDimensionsInfo(applyFilterObject, scenarioFilterKeys);
    }, [applyFilterObject, scenarioFilterKeys]);

    //reset all report details page queries
    const resetReportQueries = useCallback(() => {
        setSelectedReportInfo(null);
        setHasReportAccess(false);

        const queryKeys = [
            'pa-change-analytics-chart',
            'pa-category-movement-chart',
            'pa-single-report-details',
            'pa-lense-config-analytics',
            'pa-global-kpi-analytics',
            'pa-comparison-details',
            'pa-comparison-table-data',
            'ps-report-access',
        ];

        queryKeys.forEach((key) => {
            queryClient.removeQueries({ exact: false, queryKey: [key] });
        });
    }, [queryClient]);

    //CLEANUP
    useEffect(() => {
        //cleanup whenever report id is changing
        return () => {
            resetReportQueries();
        };
    }, [reportId, resetReportQueries]);

    useEffect(() => {
        resetFilterObject();
    }, [dimensionConfigData?.defaultDim1DropdownItem?.value]);

    const location = useLocation();
    const loggedInUser = localStorage.getItem('email') || '';

    //selected dropdown list for comparison table
    const selectedDimensionListValues = useMemo(() => {
        return scenarioComparisonState?.tableState?.dimAttributes?.map((item) => item?.value) || [];
    }, [scenarioComparisonState?.tableState?.dimAttributes]);
    //will be true if list contains product, geo
    const isDropdownAtLowestLevel =
        selectedDimensionListValues.includes(dimensionConfigData?.defaultDim1DropdownItem?.value ?? '') &&
        selectedDimensionListValues.includes(dimensionConfigData?.defaultDim2DropdownItem?.value ?? '');
    //lens config variables
    const lenseConfigVariables = {
        scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
        user_email: loggedInUser,
    };

    const globalKpisVariables = {
        kpi_ids: selectedReportInfo?.baseKPIs?.selectedKPIs,
        user_name: loggedInUser,
        base_scenario_id: selectedReportInfo?.baseScenario.scenarioId,
        report_id: selectedReportInfo?.reportId,
        filters: filters,
        is_update: isUpdate,
    };

    const isViewMode = () => mode === 'view';
    const isUpdateMode = () => mode === 'update';

    //simulator analytics index page url fetching from config
    const pricingAnalyticsIndexScreenUrl = useMemo(() => {
        const simulatorAnalyticsIndexPageRoute = screenDetailsWithRoutes?.filter((item: any) => item?.id === 'psa');
        return `/${simulatorAnalyticsIndexPageRoute?.[0]?.path}`;
    }, [screenDetailsWithRoutes]);

    //this happens if user forcefully updates url to update from view
    useEffect(() => {
        if (selectedReportInfo) {
            const areUsersSame = compareUsers(selectedReportInfo?.createdBy || '');
            if (!areUsersSame && isUpdateMode()) {
                navigate(pricingAnalyticsIndexScreenUrl);
            }
        }
    }, [selectedReportInfo?.createdBy]);

    //breadcrumb
    const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
        const defaultBreadCrumbItem = { label: 'Price & Promo Modeling' };
        const indexPageBreadCrumbLabel = 'Simulation Analytics';
        //if in index page, show breadcrumbs for index page
        if ([pricingAnalyticsIndexScreenUrl, pricingAnalyticsIndexScreenUrl].includes(location.pathname)) {
            return [defaultBreadCrumbItem, { label: indexPageBreadCrumbLabel }];
        }

        if (isViewMode() || isUpdateMode()) {
            let breadcrumbsItems: BreadcrumbItem[] = [
                defaultBreadCrumbItem,
                { label: indexPageBreadCrumbLabel, url: pricingAnalyticsIndexScreenUrl },
            ];
            //load project name to breadcrumbs once its available
            if (selectedReportInfo?.reportName) {
                breadcrumbsItems = [
                    ...breadcrumbsItems,
                    {
                        label: selectedReportInfo?.reportName,
                        icon:
                            (enablePrivatePublicToggle && getAccessIcon(selectedReportInfo?.isPrivate ?? false)) ||
                            null,
                    },
                ];
            }
            return breadcrumbsItems;
        }

        return [];
    }, [location.pathname, selectedReportInfo?.reportName]);

    const navigate = useNavigate();

    //redirect user to pricing simulator home page if mode is different from view and update
    useEffect(() => {
        if (mode && !['update', 'view'].includes(mode)) {
            navigate(pricingAnalyticsIndexScreenUrl);
        }
    }, [mode]);

    // API CALLS
    const reportAccessVariable = {
        user_id: localStorage.getItem('email'),
    };

    //get project access
    const { loading: reportAccessLoading, error: reportAccessError } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: !_.isNil(reportId) && Boolean(dimensionConfigData?.dim1DropdownList) },
        queryKey: ['ps-report-access', reportAccessVariable],
        url: `${pricingAnalyticsUrls.getReportAccess}/${reportId}/access`,
        paramsVariables: reportAccessVariable,
        parserFunction: (res: any) => {
            setHasReportAccess(true);
            return {};
        },
    });

    //redirect user to index page if error is thrown
    //if error is thrown it means user doesnt have access
    useEffect(() => {
        if (reportAccessError) {
            navigate(pricingAnalyticsIndexScreenUrl);
        }
    }, [reportAccessError]);
    // GET LENS CONFIG
    const { loading: lenseConfigLoading, error: lenseConfigError } = useFetchLenseConfigAnalytics({
        method: 'get',
        queryConfig: {
            enabled:
                Boolean(dimensionConfigData?.dim1DropdownList) &&
                Boolean(lenseConfigVariables?.scenario_id && lenseConfigVariables?.user_email),
        },
        queryKey: ['pa-lense-config-analytics', lenseConfigVariables],
        url: `${pricingSimulatorUrls.getLenseConfigUrl}`,
        variables: {
            ...lenseConfigVariables,
        },
        selectedReportInfo,
        updateReportProperty,
        setSelectedReportInfo,
    });

    const { loading: globalKpiLoading, error: globalKpiError } = useFetchGlobalKPIsData({
        method: 'post',
        queryConfig: {
            enabled: selectedReportInfo?.baseKPIs?.selectedKPIs?.length
                ? selectedReportInfo?.baseKPIs?.selectedKPIs?.length > 0
                : false,
        },
        queryKey: ['pa-global-kpi-analytics', selectedReportInfo?.baseKPIs?.selectedKPIs, globalKpisVariables],
        url: `${pricingAnalyticsUrls.getGlobalKPIsDataUrl}`,
        variables: {
            ...globalKpisVariables,
        },
        selectedReportInfo,
        updateReportProperty,
    });

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const applyFilterObj: any = {};

        scenarioFilterKeys.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            applyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(scenarioFilterKeys);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(_.cloneDeep(applyFilterObj));
        setAllSelectedValueForAllAttribute(selectAllObj);
    };

    function updateReportProperty<K extends keyof PricingReport>(key: K, value: PricingReport[K]) {
        setSelectedReportInfo((prevState: any) => {
            const updatedReport = {
                ...(prevState || {}),
                [key]: value,
            };
            return updatedReport;
        });
    }
    function updateComparisonProperty<K extends keyof ScenarioComparison>(key: K, value: ScenarioComparison[K]) {
        setScenarioComparisonState((prevState: any) => {
            const updatedComparisonState = {
                ...(prevState || {}),
                [key]: value,
            };
            return updatedComparisonState;
        });
    }

    const { mutate: updateReportFilterMutation, loading: updateReportFilterLoading } = useCommonMutationHandler({
        url: pricingAnalyticsUrls.updateReportFilter,
        method: 'post',
    });

    const saveFilterConfig = (resultObj: any) => {
        if (isUpdate) {
            // deep clone the object to api
            const filterPayload = convertSetsToArrays(_.cloneDeep(resultObj));
            const reportFilterPayload = {
                user_name: loggedInUser,
                report_id: selectedReportInfo?.reportId,
                filters: filterPayload,
            };
            updateReportFilterMutation(reportFilterPayload);
        }
    };

    return (
        <PricingAnalyticsDataContext.Provider
            value={{
                resetReportQueries,
                viewMode: isViewMode(),
                reportId: Number(reportId),
                scenarioFilter,
                setScenarioFilter,
                searchKey,
                setSearchKey,
                availableFilters,
                allCheckedAttribute,
                setAllCheckedAttribute,
                applyFilterObject,
                setApplyFilterObject,
                allSelectedValueForAllAttribute,
                setAllSelectedValueForAllAttribute,
                resetFilterObject,
                showIsEditedRecords,
                setShowIsEditedRecords,
                loggedInUser: loggedInUser,
                selectedReportInfo,
                setSelectedReportInfo,
                updateReportProperty,
                updateComparisonProperty,
                breadcrumbItems,
                showPercentageChange,
                setShowPercentageChange,
                showOldValue,
                setShowOldValue,
                lenseConfigLoading,
                globalKpiLoading,
                pricingAnalyticsIndexScreenUrl,
                isUpdate,
                filters,
                scenarioComparisonState,
                setScenarioComparisonState,
                selectedDimensionListValues,
                isDropdownAtLowestLevel,
                openScenarioPopup,
                setOpenScenarioPopup,
                saveFilterConfig,
                hasReportAccess,
                reportAccessLoading,
            }}
        >
            {children}
        </PricingAnalyticsDataContext.Provider>
    );
};

export { PricingAnalyticsDataContext, PricingAnalyticsContext };
